import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

export class StatusBarAdapter {
  private static instance: StatusBarAdapter;
  private isInitialized: boolean = false;
  private themeObserver: MutationObserver | null = null;
  private themeUpdateTimer: number | null = null;

  static getInstance(): StatusBarAdapter {
    if (!StatusBarAdapter.instance) {
      StatusBarAdapter.instance = new StatusBarAdapter();
    }
    return StatusBarAdapter.instance;
  }

  async init(options?: { overlaysWebView?: boolean }): Promise<void> {
    if (this.isInitialized) return;

    try {
      if (Capacitor.isNativePlatform()) {
        await StatusBar.hide();
        const info = await StatusBar.getInfo();
        console.log('状态栏初始信息:', info);

        const targetStyle = this.getOptimalStyle(info);

        // 设置状态栏字体/图标颜色
        await StatusBar.setStyle({ style: targetStyle });

        // 背景透明
        await StatusBar.setBackgroundColor({ color: '#00000000' });

        // 统一采用覆盖 WebView，避免不同页面切换 overlay 造成布局跳动
        const overlay = options?.overlaysWebView ?? true;
        await StatusBar.setOverlaysWebView({ overlay });

        // 显示状态栏
        await StatusBar.show();

        // 安全区域适配
        this.applySafeAreaAdaptation();

        // 监听主题变化，及时同步状态栏字体/图标颜色
        this.startThemeObserver();

        const finalInfo = await StatusBar.getInfo();
        console.log('状态栏最终信息:', finalInfo);
      } else {
        console.log('Web 平台，跳过状态栏设置');
      }

      this.isInitialized = true;
      console.log('状态栏适配器初始化完成');
    } catch (error) {
      console.warn('状态栏适配器初始化失败:', error);
      await this.fallbackSetup();
    }
  }

  private startThemeObserver(): void {
    if (!Capacitor.isNativePlatform()) return;
    if (this.themeObserver) return;

    const scheduleUpdate = () => {
      if (this.themeUpdateTimer != null) window.clearTimeout(this.themeUpdateTimer);
      this.themeUpdateTimer = window.setTimeout(() => {
        this.updateStyleForCurrentPage().catch((e) => {
          console.warn('主题变化后更新状态栏失败:', e);
        });
      }, 50);
    };

    this.themeObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes') continue;
        if (m.attributeName === 'theme-mode' || m.attributeName === 'class' || m.attributeName === 'style') {
          scheduleUpdate();
          return;
        }
      }
    });

    try {
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['theme-mode', 'class', 'style']
      });
      if (document.body) {
        this.themeObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    } catch (e) {
      console.warn('启动主题监听失败:', e);
    }
  }

  /** 根据主题智能选择字体颜色 */
  private getOptimalStyle(currentInfo: any): Style {
    const darkTheme = this.isAppDarkTheme();
    const lightTheme = this.isAppLightTheme();
    const platform = Capacitor.getPlatform();

    if (platform === 'ios') {
      // iOS: Light -> 白字（用于深色背景）, Dark -> 黑字（用于浅色背景）
      if (darkTheme) return Style.Dark;  // 深色背景用白色字体
      if (lightTheme) return Style.Light;   // 浅色背景用黑色字体
      return currentInfo?.style || Style.Dark;
    } else {
      // Android: Light -> 深色字体（用于浅色背景）, Dark -> 白色字体（用于深色背景）
      // Style.Light = setAppearanceLightStatusBars(true) = 深色字体
      // Style.Dark = setAppearanceLightStatusBars(false) = 白色字体
      if (darkTheme) return Style.Dark;   // 深色背景用白色字体
      if (lightTheme) return Style.Light; // 浅色背景用深色字体
      return currentInfo?.style || Style.Light; // 默认浅色背景用深色字体
    }
  }

  /** 降级方案 */
  private async fallbackSetup(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#00000000' });
        await StatusBar.show();
        this.applyBasicSafeArea();
      }
    } catch (error) {
      console.error('降级方案失败:', error);
    }
  }

  /** 是否深色主题 */
  private isAppDarkTheme(): boolean {
    const root = document.documentElement;
    const themeMode = root.getAttribute('theme-mode');
    if (themeMode === 'dark') return true;
    if (root.classList.contains('var-dark')) return true;

    const body = document.body;
    if (body.classList.contains('dark-theme') || body.classList.contains('dark')) return true;
    const bodyColor = getComputedStyle(body).backgroundColor;
    if (this.isColorDark(bodyColor)) return true;
    const appEl = document.getElementById('app');
    if (appEl && this.isColorDark(getComputedStyle(appEl).backgroundColor)) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /** 是否浅色主题 */
  private isAppLightTheme(): boolean {
    const root = document.documentElement;
    const themeMode = root.getAttribute('theme-mode');
    if (themeMode && themeMode !== 'dark') return true;
    if (root.classList.contains('var-dark')) return false;

    const body = document.body;
    if (body.classList.contains('light-theme') || body.classList.contains('light')) return true;
    const bodyColor = getComputedStyle(body).backgroundColor;
    if (this.isColorLight(bodyColor)) return true;
    const appEl = document.getElementById('app');
    if (appEl && this.isColorLight(getComputedStyle(appEl).backgroundColor)) return true;
    return window.matchMedia('(prefers-color-scheme: light)').matches;
  }

  private applyBasicSafeArea(): void {
    const root = document.documentElement;
    // 说明：CSS 已在 :root 中定义了 --safe-area-inset-top = env(safe-area-inset-top)
    // 为避免覆盖 iOS 刘海/灵动岛设备的真实 safe-area，这里仅在 env() 不可用（计算结果为 0px）时才使用固定兜底值。
    const computedTop = getComputedStyle(root).getPropertyValue('--safe-area-inset-top').trim();
    if (!computedTop || computedTop === '0px') {
      root.style.setProperty('--safe-area-inset-top', '24px');
    }
    const computedBottom = getComputedStyle(root).getPropertyValue('--safe-area-inset-bottom').trim();
    if (!computedBottom || computedBottom === '0px') {
      root.style.setProperty('--safe-area-inset-bottom', '0px');
    }
  }

  private applySafeAreaAdaptation(): void {
    const appEl = document.getElementById('app');
    if (appEl) {
      appEl.classList.add('needs-status-bar-adaptation');
      this.forceSetSafeAreaVariables();
    }
  }

  private forceSetSafeAreaVariables(): void {
    const root = document.documentElement;
    // 同上：仅在 env() 无法提供有效值时才写入兜底，避免把 iOS 的真实 safe-area 错误写死成 24px。
    const computedTop = getComputedStyle(root).getPropertyValue('--safe-area-inset-top').trim();
    if (!computedTop || computedTop === '0px') {
      root.style.setProperty('--safe-area-inset-top', '24px');
    }
    const computedBottom = getComputedStyle(root).getPropertyValue('--safe-area-inset-bottom').trim();
    if (!computedBottom || computedBottom === '0px') {
      root.style.setProperty('--safe-area-inset-bottom', '0px');
    }
    const computedLeft = getComputedStyle(root).getPropertyValue('--safe-area-inset-left').trim();
    if (!computedLeft || computedLeft === '0px') {
      root.style.setProperty('--safe-area-inset-left', '0px');
    }
    const computedRight = getComputedStyle(root).getPropertyValue('--safe-area-inset-right').trim();
    if (!computedRight || computedRight === '0px') {
      root.style.setProperty('--safe-area-inset-right', '0px');
    }
  }

  private isColorDark(color: string | null): boolean {
    const rgb = this.parseColor(color);
    if (!rgb) return false;
    const luminance = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
    return luminance < 128;
  }

  private isColorLight(color: string | null): boolean {
    const rgb = this.parseColor(color);
    if (!rgb) return false;
    const luminance = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
    return luminance >= 200;
  }

  private parseColor(color: string | null): { r: number; g: number; b: number } | null {
    if (!color) return null;
    const value = color.trim().toLowerCase();
    const rgbMatch = value.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch)
      return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) };
    if (value.startsWith('#')) {
      let hex = value.slice(1);
      if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
      if (hex.length === 6)
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16)
        };
    }
    return null;
  }

  async getStatusBarInfo(): Promise<any> {
    if (Capacitor.isNativePlatform()) {
      try {
        return await StatusBar.getInfo();
      } catch {
        return null;
      }
    }
    return null;
  }

  /** 根据路由路径自动设置状态栏样式 */
  async setStatusBarForRoute(routePath: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
      // 始终保持透明背景
      await StatusBar.setBackgroundColor({ color: '#00000000' });

      // 根据路由类型仅调整字体/图标颜色，不再切换 overlay，避免布局跳动
      const isLoginOrAd = routePath === '/login' || routePath === '/ad';
      const isRegisterOrForgot = routePath === '/register' || routePath === '/forgotPassword';

      if (isLoginOrAd) {
        // 登录/广告页支持主题切换：根据当前主题选择字体/图标颜色
        const targetStyle = this.getOptimalStyle(null);
        await StatusBar.setStyle({ style: targetStyle });
      } else if (isRegisterOrForgot) {
        // 注册/忘记密码页支持主题切换：根据当前选择字体/图标颜色
        const targetStyle = this.getOptimalStyle(null);
        await StatusBar.setStyle({ style: targetStyle });
      } else {
        // 其它主页面依据主题选择
        const targetStyle = this.getOptimalStyle(null);
        await StatusBar.setStyle({ style: targetStyle });
      }

      // 确保状态栏显示
      await StatusBar.show();
    } catch (error) {
      console.warn('根据路由设置状态栏失败:', error);
    }
  }

  /** 根据当前页面主题自动调整状态栏样式 */
  async updateStyleForCurrentPage(): Promise<void> {
    // 获取当前路由路径
    const currentPath = window.location.pathname;
    await this.setStatusBarForRoute(currentPath);
  }

//   debug(): void {
//     console.group('状态栏适配器 调试信息');
//     console.log('已初始化:', this.isInitialized);
//     console.log('平台:', Capacitor.getPlatform());
//     console.log('是原生平台:', Capacitor.isNativePlatform());
//     const rootStyle = getComputedStyle(document.documentElement);
//     console.log('CSS变量值:', {
//       '--safe-area-inset-top': rootStyle.getPropertyValue('--safe-area-inset-top'),
//       '--safe-area-inset-bottom': rootStyle.getPropertyValue('--safe-area-inset-bottom')
//     });
//     console.groupEnd();
//   }
}

export const statusBarAdapter = StatusBarAdapter.getInstance();
